'use client';
import React, { useState, useCallback, useEffect } from 'react';
import { CourseCard, CourseOffer } from '@/components/molecules/CourseCard';
import { useRouter } from 'next/navigation';
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from '@/components/atoms/carousel';
import HighlightedHeading from '@/components/molecules/HighlightedHeading';

interface CourseOffersCarouselProps {
  title?: string;
  subtitle?: string;
  addButtonText?: string;
  applyButtonText?: string;
  courses: CourseOffer[];
  accentColor?: string;
  className?: string;
}

// Re-export CourseOffer type for convenience
export type { CourseOffer };

export const CourseOffersCarousel: React.FC<CourseOffersCarouselProps> = ({
  title = 'Fique a par das nossas Ofertas',
  subtitle = 'Ofertas Formativas',
  addButtonText = '+ Ofertas',
  applyButtonText = 'Candidatar',
  courses,
  className = ''
}) => {
  const router = useRouter();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [carouselCurrentIndex, setCarouselCurrentIndex] = useState(0);
  const [carouselCount, setCarouselCount] = useState(0);
  const itemsPerPage = 3;
  const totalPages = Math.ceil(courses.length / itemsPerPage);

  // Carousel API handlers
  useEffect(() => {
    if (!carouselApi) return;

    setCarouselCount(carouselApi.scrollSnapList().length);
    setCarouselCurrentIndex(carouselApi.selectedScrollSnap());

    carouselApi.on('select', () => {
      setCarouselCurrentIndex(carouselApi.selectedScrollSnap());
    });
  }, [carouselApi]);

  const scrollToIndex = useCallback((index: number) => {
    carouselApi?.scrollTo(index);
  }, [carouselApi]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };
  const visibleCourses = courses.slice(
    currentIndex * itemsPerPage,
    (currentIndex + 1) * itemsPerPage
  );

  return (
    <div className={`w-full container mx-auto py-16 px-4 md:px-8 lg:px-12 ${className}`}>
      <div className="flex flex-col lg:flex-row gap-8 items-start">
        {/* Left Content - Title Section */}
        <div className="lg:w-1/4 space-y-6 flex-shrink-0">
          <div className="max-w-[270px]">
            <HighlightedHeading text={title || ''} positions={[0]} />
          </div>

          <div className="inline-block bg-stone-300/20 rounded-[30px] px-4 py-2">
            <span className="text-gray-700 text-base font-normal font-['Poppins']">
              {subtitle}
            </span>
          </div>

          <button className="hidden lg:flex bg-[#F05A49] hover:bg-[#d94839] rounded-[30px] px-8 py-2.5 items-center gap-2 transition-colors cursor-pointer" onClick={() => router.push('/ofertas-formativas')}>
            <span className="text-white text-2xl font-medium font-['Poppins']">+</span>
            <span className="text-white text-lg font-medium font-['Poppins'] capitalize">
              {addButtonText.replace('+ ', '')}
            </span>
          </button>
        </div>

        {/* Right Content - Cards Carousel */}
        <div className="w-full lg:w-3/4 relative">
          {/* Desktop: Grid com paginação (apenas lg+) */}
          <div className="hidden lg:block">
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
              {visibleCourses.map((course) => (
                <CourseCard
                  key={course.id}
                  course={course}
                  applyButtonText={applyButtonText}
                />
              ))}
            </div>
            {/* Pagination Dots - Desktop */}
            {totalPages > 1 && (
              <div className="flex justify-start gap-3 mt-8">
                {Array.from({ length: totalPages }).map((_, index) => (
                  <button
                    key={`ofertas-pagination-${index}`}
                    onClick={() => goToSlide(index)}
                    className={`cursor-pointer rounded-full transition-all ${index === currentIndex
                        ? 'w-12 h-3 bg-[#377396]'
                        : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                      }`}
                    aria-label={`Ir para página ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Mobile e Tablet: Carrossel */}
          <div className="lg:hidden">
            <Carousel className="w-full" setApi={setCarouselApi}>
              <CarouselContent className="-ml-2 md:-ml-4">
                {courses.map((course) => (
                  <CarouselItem key={course.id} className="pl-2 md:pl-4 basis-[85%] sm:basis-[70%] md:basis-1/2">
                    <CourseCard
                      course={course}
                      applyButtonText={applyButtonText}
                    />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
            {/* Pagination Dots - Mobile e Tablet */}
            {carouselCount > 1 && (
              <div className="flex justify-center gap-2 sm:gap-3 mt-6">
                {Array.from({ length: carouselCount }).map((_, index) => (
                  <button
                    key={`carousel-dot-${index}`}
                    onClick={() => scrollToIndex(index)}
                    className={`cursor-pointer rounded-full transition-all ${index === carouselCurrentIndex
                        ? 'w-10 sm:w-12 h-3 bg-[#377396]'
                        : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                      }`}
                    aria-label={`Ir para slide ${index + 1}`}
                  />
                ))}
              </div>
            )}
            {/* Botão Ver Ofertas - Mobile */}
            <div className="flex justify-center mt-8">
              <button className="bg-[#F05A49] hover:bg-[#d94839] rounded-[30px] px-8 py-2.5 flex items-center gap-2 transition-colors cursor-pointer" onClick={() => router.push('/ofertas-formativas')}>
                <span className="text-white text-2xl font-medium font-['Poppins']">+</span>
                <span className="text-white text-lg font-medium font-['Poppins'] capitalize">
                  {addButtonText.replace('+ ', '')}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>


    </div>
  );
};
